import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useTranslation } from "react-i18next"

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@f2e/ui/components/base"

import {
  AuthLinkResultCard,
  PasswordBasicErrorMessage,
  PasswordRequirements,
} from "@/components/Auth"
import { FetchStatus, useToast } from "@/hooks"
import {
  usePasswordResetConfirmApi,
  usePasswordResetStatusApi,
} from "@/hooks/api/account"
import type { ApiErrorWithData } from "@/lib/authErrors"
import { getAuthPasswordErrorMessage } from "@/lib/authErrors"
import { getNewPasswordFormError } from "@/lib/passwordValidation"

type ResetLinkStatus = "loading" | "valid" | "invalid" | "unavailable"

const ResetPassword = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { alertToast } = useToast()
  const {
    status: resetStatusFetchStatus,
    data: resetStatusData,
    error: resetStatusError,
    fetchFn: fetchResetStatus,
  } = usePasswordResetStatusApi()
  const { fetchFn: confirmReset } = usePasswordResetConfirmApi()
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [linkStatus, setLinkStatus] = useState<ResetLinkStatus>("loading")
  const [message, setMessage] = useState(t("common.auth.reset.validating"))

  const uid = searchParams.get("uid")
  const token = searchParams.get("token")

  const goToLogin = () => navigate("/login")
  const goToForgotPassword = () => navigate("/forgot-password")

  const validateResetLink = useCallback(() => {
    if (!uid || !token) {
      setLinkStatus("invalid")
      setMessage(t("common.auth.reset.errors.missingInfo"))
      return
    }

    setLinkStatus("loading")
    setMessage(t("common.auth.reset.validating"))

    void fetchResetStatus({
      jsonBody: {
        uid,
        token,
      },
    }).catch(() => undefined)
  }, [fetchResetStatus, uid, token, t])

  useEffect(() => {
    validateResetLink()
  }, [validateResetLink])

  useEffect(() => {
    if (resetStatusFetchStatus !== FetchStatus.Success || !resetStatusData) {
      return
    }
    setLinkStatus("valid")
    setMessage(t("common.auth.reset.description"))
  }, [resetStatusData, resetStatusFetchStatus, t])

  useEffect(() => {
    if (resetStatusFetchStatus !== FetchStatus.Error || !resetStatusError) {
      return
    }
    const apiError = resetStatusError as ApiErrorWithData
    if (apiError.response?.status === 400) {
      setLinkStatus("invalid")
      setMessage(t("common.auth.reset.errors.invalidOrExpired"))
      return
    }

    setLinkStatus("unavailable")
    setMessage(
      apiError.response?.status === 429
        ? t("common.auth.errors.tooManyRequests")
        : t("common.auth.reset.errors.validationFailed")
    )
  }, [resetStatusError, resetStatusFetchStatus, t])

  const handleSubmit = async () => {
    if (!uid || !token) {
      alertToast({
        title: t("common.auth.reset.errors.title"),
        description: t("common.auth.reset.errors.missingInfo"),
      })
      return
    }

    const passwordError = getNewPasswordFormError({
      password,
      confirmPassword,
    })
    if (passwordError) {
      const errorMessages = {
        passwordRequired: t("common.auth.reset.errors.passwordRequired"),
        confirmPasswordRequired: t(
          "common.auth.reset.errors.confirmPasswordRequired"
        ),
        passwordsDoNotMatch: t("common.auth.errors.passwordsDoNotMatch"),
        passwordPolicy: t("common.auth.errors.passwordPolicy"),
      }
      alertToast({
        title: t("common.auth.reset.errors.title"),
        description: errorMessages[passwordError],
      })
      return
    }

    try {
      await confirmReset({
        jsonBody: {
          uid,
          token,
          new_password: password,
        },
      })
      localStorage.removeItem("loggedIn")
      setSubmitted(true)
    } catch (error) {
      const message = getAuthPasswordErrorMessage(
        error,
        t,
        "common.auth.reset.errors.confirmFailed"
      )
      alertToast({
        title: t("common.auth.reset.errors.title"),
        description: message,
      })
    }
  }

  if (submitted) {
    return (
      <div className="container flex min-h-[600px] items-center justify-center">
        <AuthLinkResultCard
          title={t("common.auth.reset.title")}
          description={t("common.auth.reset.success")}
          variant="success"
          primaryAction={{
            label: t("common.auth.actions.backToLogin"),
            onClick: goToLogin,
          }}
        />
      </div>
    )
  }

  if (linkStatus !== "valid") {
    return (
      <div className="container flex min-h-[600px] items-center justify-center">
        <AuthLinkResultCard
          title={t("common.auth.reset.title")}
          description={message}
          variant={linkStatus === "loading" ? "loading" : "error"}
          primaryAction={
            linkStatus === "loading"
              ? undefined
              : linkStatus === "unavailable"
                ? {
                    label: t("common.auth.actions.retry"),
                    onClick: validateResetLink,
                  }
                : {
                    label: t("common.auth.actions.requestNewResetLink"),
                    onClick: goToForgotPassword,
                  }
          }
          secondaryAction={
            linkStatus === "loading"
              ? undefined
              : {
                  label: t("common.auth.actions.backToLogin"),
                  onClick: goToLogin,
                }
          }
        />
      </div>
    )
  }

  return (
    <div className="container flex min-h-[600px] items-center justify-center">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {t("common.auth.reset.title")}
          </CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="password">
                  {t("common.auth.fields.newPassword")}
                </Label>
                <PasswordRequirements />
              </div>
              <Input
                id="password"
                type="password"
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <PasswordBasicErrorMessage
                password={password}
                show={password.length > 0}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm-password">
                {t("common.auth.fields.confirmPassword")}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>
            <Button className="w-full" onClick={handleSubmit}>
              {t("common.auth.reset.submit")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ResetPassword
